import React, { useState, useEffect, useCallback } from 'react';
import Topbar from '../components/Topbar';
import Modal from '../components/Modal';
import api, { API } from '../utils/api';
import { MagnifyingGlass, PencilSimple, Trash, SpinnerGap, Globe } from '@phosphor-icons/react';
import { toast } from 'sonner';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import usePullToRefresh from '../hooks/usePullToRefresh';
import PullToRefreshIndicator from '../components/PullToRefreshIndicator';
import useContentHeight from '../hooks/useContentHeight';

const Dominios = () => {
  const contentHeight = useContentHeight(280); // Increased for stat card
  const [dominios, setDominios] = useState([]);
  const [filteredDominios, setFilteredDominios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingDominio, setEditingDominio] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('todos');
  const [dominiosStats, setDominiosStats] = useState({ total: 0, porVencer: 0 });
  
  const [formData, setFormData] = useState({
    nombre: '',
    tipo: '.com',
    registrar: 'GoDaddy',
    cliente_id: '',
    anualidad: 0,
    periodo: 'anual',
    fecha_registro: '',
    estado: '',
    host: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterDominios();
  }, [dominios, searchTerm, filter]);

  const fetchData = async () => {
    try {
      const [dominiosRes, clientesRes] = await Promise.all([
        api.get('/dominios'),
        api.get('/clientes')
      ]);
      setDominios(dominiosRes.data);
      setClientes(clientesRes.data);
      
      // Calcular estadísticas de dominios
      const total = dominiosRes.data.length;
      const now = new Date();
      const sixtyDaysLater = new Date(now.getTime() + 60 * 24 * 60 * 60 * 1000);
      
      let porVencer = 0;
      dominiosRes.data.forEach(d => {
        if (d.fecha_vencimiento && d.estado !== 'pagado') {
          const fechaVenc = new Date(d.fecha_vencimiento);
          if (fechaVenc >= now && fechaVenc <= sixtyDaysLater) {
            porVencer++;
          }
        }
      });
      
      setDominiosStats({ total, porVencer });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const filterDominios = () => {
    let filtered = [...dominios];

    if (searchTerm) {
      filtered = filtered.filter(d =>
        d.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (d.cliente_nombre && d.cliente_nombre.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (filter === 'pagados') {
      // Mostrar solo dominios pagados
      filtered = filtered.filter(d => d.estado === 'pagado');
    } else if (filter === 'vencidos') {
      filtered = filtered.filter(d => d.estado === 'vencido');
    } else if (filter === 'todos') {
      // En vista "todos", excluir pagados e inactivos
      filtered = filtered.filter(d => d.estado !== 'pagado' && d.estado !== 'inactivo');
    }

    // Excluir inactivos en todas las vistas excepto pagados
    if (filter !== 'pagados') {
      filtered = filtered.filter(d => d.estado !== 'inactivo');
    }

    // Ordenar por fecha de vencimiento más próxima primero
    filtered.sort((a, b) => {
      const dateA = new Date(a.fecha_vencimiento);
      const dateB = new Date(b.fecha_vencimiento);
      return dateA - dateB;
    });

    setFilteredDominios(filtered);
  };

  // Infinite scroll for mobile
  const { 
    displayedItems: displayedDominios, 
    hasMore, 
    isLoadingMore, 
    loadMoreRef,
    totalItems,
    displayedCount 
  } = useInfiniteScroll(filteredDominios, 8);

  // Pull to refresh for mobile
  const handleRefresh = useCallback(async () => {
    await fetchData();
    toast.success('Datos actualizados');
  }, []);

  const {
    containerRef,
    pullDistance,
    pullProgress,
    isRefreshing
  } = usePullToRefresh(handleRefresh);

  const getExpiryPercentage = (fechaVencimiento) => {
    const today = new Date();
    const expiry = new Date(fechaVencimiento);
    const oneYearAgo = new Date(expiry);
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    
    const totalDays = (expiry - oneYearAgo) / (1000 * 60 * 60 * 24);
    const daysLeft = (expiry - today) / (1000 * 60 * 60 * 24);
    
    return Math.max(0, Math.min(100, (daysLeft / totalDays) * 100));
  };

  const getExpiryColor = (percentage) => {
    if (percentage > 50) return '#00d68f';
    if (percentage > 25) return '#ffc84a';
    return '#ff5252';
  };

  const handleAdd = () => {
    setEditingDominio(null);
    const today = new Date().toISOString().split('T')[0];
    
    setFormData({
      nombre: '',
      tipo: '.com',
      registrar: 'Akky',
      cliente_id: clientes.length > 0 ? [...clientes].sort((a, b) => a.nombre.localeCompare(b.nombre))[0]?.id : '',
      anualidad: 350,
      periodo: 'anual',
      fecha_registro: today,
      estado: '',
      host: ''
    });
    setShowModal(true);
  };

  const handleEdit = (dominio) => {
    setEditingDominio(dominio);
    setFormData({
      nombre: dominio.nombre || '',
      tipo: dominio.tipo || '.com',
      registrar: dominio.registrar || 'Akky',
      cliente_id: dominio.cliente_id || '',
      anualidad: dominio.anualidad || 0,
      periodo: dominio.periodo || 'anual',
      fecha_registro: dominio.fecha_registro || '',
      estado: dominio.estado || '',
      host: dominio.host || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (dominioId) => {
    if (!window.confirm('¿Estás seguro de eliminar este dominio?')) return;

    try {
      await api.delete(`/dominios/${dominioId}`);
      toast.success('Dominio eliminado exitosamente');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar dominio');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingDominio) {
        await api.put(`/dominios/${editingDominio.id}`, formData);
        toast.success('Dominio actualizado exitosamente');
      } else {
        await api.post('/dominios', formData);
        toast.success('Dominio creado exitosamente');
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error('Error al guardar dominio');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('token');
      window.open(`${API}/export/dominios?token=${token}`, '_blank');
      toast.success('Exportando dominios...');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#0d0f14]">
        <div className="text-[#4f9eff] font-['IBM_Plex_Mono'] text-lg">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto bg-[#0d0f14] min-h-screen" ref={containerRef}>
      <Topbar
        title="Dominios"
        breadcrumb="Visionweb / Módulos / Dominios"
        onAdd={handleAdd}
        onExport={handleExport}
        showAdd={true}
        showExport={true}
      />

      <div className="p-4 md:p-6 pb-12">
        {/* Pull to Refresh Indicator - Mobile only */}
        <div className="block md:hidden">
          <PullToRefreshIndicator 
            pullDistance={pullDistance}
            pullProgress={pullProgress}
            isRefreshing={isRefreshing}
          />
        </div>

        {/* Stat Card - Dominios */}
        <div className="bg-[#13161e] border border-[#2a2e3d] rounded-md p-4 mb-4 flex items-center gap-4">
          <div className="p-3 bg-[#4f9eff]/10 rounded-md">
            <Globe size={24} className="text-[#4f9eff]" weight="duotone" />
          </div>
          <div className="flex-1">
            <div className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase">Total Dominios</div>
            <div className="text-white font-['Fraunces'] text-2xl font-bold">{dominiosStats.total}</div>
          </div>
          <div className="text-right">
            <div className="text-[#ffc84a] font-['IBM_Plex_Mono'] text-sm font-bold">{dominiosStats.porVencer}</div>
            <div className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs">Por vencer (60d)</div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4 mb-4 md:mb-6">
          <div className="flex-1 relative">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8b949e]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar dominios..."
              className="w-full bg-[#13161e] border border-[#2a2e3d] rounded-sm pl-10 pr-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 sm:pb-0">
            {['todos', 'pagados', 'vencidos'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 md:px-4 py-2 rounded-sm font-['IBM_Plex_Mono'] text-xs whitespace-nowrap transition-colors ${
                  filter === f
                    ? 'bg-[#4f9eff] text-[#0d0f14] font-bold'
                    : 'bg-[#13161e] border border-[#2a2e3d] text-[#8b949e] hover:text-white'
                }`}
                data-testid={`filter-${f}`}
              >
                {f === 'todos' ? 'Todos' : f === 'pagados' ? 'Pagados' : 'Vencidos'}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile Cards View with Infinite Scroll */}
        <div className="block md:hidden overflow-y-auto space-y-3 pr-1" style={{ maxHeight: contentHeight }}>
          {displayedDominios.map((dominio) => {
            const percentage = getExpiryPercentage(dominio.fecha_vencimiento);
            const color = getExpiryColor(percentage);
            
            return (
              <div key={dominio.id} className="bg-[#13161e] border border-[#2a2e3d] rounded-md p-4" data-testid={`dominio-card-${dominio.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-['IBM_Plex_Mono'] text-sm font-bold truncate">{dominio.nombre}</div>
                    <div className="text-[#8b949e] font-['Mulish'] text-xs truncate">{dominio.cliente_nombre || '-'}</div>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 border rounded-sm font-['IBM_Plex_Mono'] text-[10px] ml-2 ${
                    dominio.estado === 'activo' ? 'bg-[#00d68f]/10 border-[#00d68f] text-[#00d68f]' :
                    dominio.estado === 'por vencer' ? 'bg-[#ffc84a]/10 border-[#ffc84a] text-[#ffc84a]' :
                    'bg-[#ff5252]/10 border-[#ff5252] text-[#ff5252]'
                  }`}>
                    {dominio.estado}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs mb-2">
                  <span className="text-[#8b949e] font-['IBM_Plex_Mono']">{dominio.registrar}</span>
                  <span className="text-white font-['IBM_Plex_Mono'] font-bold">${dominio.anualidad.toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs">{dominio.fecha_vencimiento}</span>
                  <div className="flex-1 h-1 bg-[#2a2e3d] rounded-full overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${percentage}%`, backgroundColor: color }} />
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[#2a2e3d]">
                  <button
                    onClick={() => handleEdit(dominio)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#4f9eff]/10 border border-[#4f9eff] text-[#4f9eff] rounded-sm text-xs font-['IBM_Plex_Mono']"
                    data-testid={`edit-dominio-${dominio.id}`}
                  >
                    <PencilSimple size={14} /> Editar
                  </button>
                  <button
                    onClick={() => handleDelete(dominio.id)}
                    className="px-3 py-2 bg-[#ff5252]/10 border border-[#ff5252] text-[#ff5252] rounded-sm"
                    data-testid={`delete-dominio-${dominio.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            );
          })}
          
          {/* Load more trigger & indicator */}
          {hasMore && (
            <div ref={loadMoreRef} className="flex justify-center py-4">
              {isLoadingMore ? (
                <div className="flex items-center gap-2 text-[#4f9eff]">
                  <SpinnerGap size={20} className="animate-spin" />
                  <span className="font-['IBM_Plex_Mono'] text-xs">Cargando más...</span>
                </div>
              ) : (
                <div className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs">
                  Mostrando {displayedCount} de {totalItems}
                </div>
              )}
            </div>
          )}
          
          {!hasMore && displayedDominios.length > 0 && (
            <div className="text-center py-4 text-[#8b949e] font-['IBM_Plex_Mono'] text-xs">
              {totalItems} dominios en total
            </div>
          )}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block bg-[#13161e] border border-[#2a2e3d] rounded-md overflow-hidden">
          <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: 'calc(100vh - 250px)' }}>
            <table className="w-full min-w-[900px]">
              <thead className="bg-[#0d0f14] border-b border-[#2a2e3d] sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase whitespace-nowrap">Dominio</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase whitespace-nowrap">Register</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase whitespace-nowrap">Vencimiento</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase whitespace-nowrap">Host</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase whitespace-nowrap">Anualidad</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase whitespace-nowrap">Estado</th>
                  <th className="px-4 py-3 text-left text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase whitespace-nowrap">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2a2e3d]">
                {filteredDominios.map((dominio) => {
                  const percentage = getExpiryPercentage(dominio.fecha_vencimiento);
                  const color = getExpiryColor(percentage);
                  
                  return (
                    <tr key={dominio.id} className="hover:bg-[#1a1e28] transition-colors group" data-testid={`dominio-row-${dominio.id}`}>
                      <td className="px-4 py-4 text-white font-['IBM_Plex_Mono'] text-sm font-bold whitespace-nowrap">{dominio.nombre}</td>
                      <td className="px-4 py-4 text-[#8b949e] font-['IBM_Plex_Mono'] text-sm whitespace-nowrap">{dominio.registrar}</td>
                      <td className="px-4 py-4">
                        <div className="space-y-1">
                          <div className="text-white font-['IBM_Plex_Mono'] text-sm whitespace-nowrap">
                            {dominio.estado === 'inactivo' ? '0000-00-00' : dominio.fecha_vencimiento}
                          </div>
                          {dominio.estado !== 'inactivo' && (
                            <div className="h-1 w-20 bg-[#2a2e3d] rounded-full overflow-hidden">
                              <div 
                                className="h-full transition-all" 
                                style={{ width: `${percentage}%`, backgroundColor: color }}
                              />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-[#8b949e] font-['IBM_Plex_Mono'] text-sm whitespace-nowrap">{dominio.host || '-'}</td>
                      <td className="px-4 py-4 text-white font-['IBM_Plex_Mono'] text-sm font-bold whitespace-nowrap">
                        ${dominio.anualidad.toFixed(2)}
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2 py-1 border rounded-sm font-['IBM_Plex_Mono'] text-xs whitespace-nowrap ${
                          dominio.estado === 'activo' ? 'bg-[#00d68f]/10 border-[#00d68f] text-[#00d68f]' :
                          dominio.estado === 'por vencer' ? 'bg-[#ffc84a]/10 border-[#ffc84a] text-[#ffc84a]' :
                          'bg-[#ff5252]/10 border-[#ff5252] text-[#ff5252]'
                        }`}>
                          {dominio.estado}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleEdit(dominio)}
                            className="text-[#4f9eff] hover:text-white transition-colors"
                            data-testid={`edit-dominio-${dominio.id}`}
                          >
                            <PencilSimple size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(dominio.id)}
                            className="text-[#ff5252] hover:text-white transition-colors"
                            data-testid={`delete-dominio-${dominio.id}`}
                          >
                            <Trash size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {filteredDominios.length === 0 && (
          <div className="text-center py-12">
            <div className="text-[#8b949e] font-['Mulish'] text-sm">No se encontraron dominios</div>
          </div>
        )}
      </div>

      {/* Modal Form */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingDominio ? 'Editar Dominio' : 'Agregar Dominio'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Línea 1: Nombre del Dominio | Cliente */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Nombre del Dominio *</label>
              <input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                placeholder="ejemplo.com"
                required
                data-testid="form-nombre"
              />
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Cliente *</label>
              <select
                value={formData.cliente_id}
                onChange={(e) => setFormData({ ...formData, cliente_id: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                required
                data-testid="form-cliente"
              >
                <option value="">Seleccionar cliente</option>
                {[...clientes].sort((a, b) => a.nombre.localeCompare(b.nombre)).map(cliente => (
                  <option key={cliente.id} value={cliente.id}>{cliente.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Línea 2: Register | Fecha de Registro */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Register *</label>
              <select
                value={formData.registrar}
                onChange={(e) => setFormData({ ...formData, registrar: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                required
                data-testid="form-registrar"
              >
                <option value="Akky">Akky</option>
                <option value="GoDaddy">GoDaddy</option>
                <option value="Google Domains">Google Domains</option>
                <option value="HostPapa">HostPapa</option>
                <option value="Namecheap">Namecheap</option>
                <option value="Neubox">Neubox</option>
                <option value="SiteGround">SiteGround</option>
                <option value="Suempresa">Suempresa</option>
              </select>
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Fecha de Registro *</label>
              <input
                type="date"
                value={formData.fecha_registro}
                onChange={(e) => setFormData({ ...formData, fecha_registro: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                required
                data-testid="form-fecha-registro"
              />
              <div className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs mt-1">
                Vencimiento = Registro + Periodo
              </div>
            </div>
          </div>

          {/* Línea 3: Anualidad | Periodo */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Anualidad *</label>
              <input
                type="number"
                step="0.01"
                value={formData.anualidad}
                onChange={(e) => setFormData({ ...formData, anualidad: parseFloat(e.target.value) })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                required
                data-testid="form-anualidad"
              />
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Periodo *</label>
              <select
                value={formData.periodo}
                onChange={(e) => setFormData({ ...formData, periodo: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                required
                data-testid="form-periodo"
              >
                <option value="anual">Anual</option>
                <option value="2años">2 Años</option>
                <option value="3años">3 Años</option>
              </select>
            </div>
          </div>

          {/* Línea 4: Estado | Host */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Estado</label>
              <select
                value={formData.estado}
                onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                data-testid="form-estado"
              >
                <option value="">Automático</option>
                <option value="activo">Activo</option>
                <option value="por vencer">Por Vencer</option>
                <option value="vencido">Vencido</option>
                <option value="pagado">Pagado</option>
              </select>
            </div>
            <div>
              <label className="text-[#8b949e] font-['IBM_Plex_Mono'] text-xs uppercase block mb-2">Host</label>
              <select
                value={formData.host}
                onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                className="w-full bg-[#0d0f14] border border-[#2a2e3d] rounded-sm px-4 py-2 text-white font-['Mulish'] text-sm focus:border-[#4f9eff] focus:outline-none"
                data-testid="form-host"
              >
                <option value="">Seleccionar host</option>
                <option value="NEUBOX">NEUBOX</option>
                <option value="SITEGROUND">SITEGROUND</option>
              </select>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="px-4 py-2 bg-transparent border border-[#2a2e3d] text-white rounded-sm hover:border-[#4f9eff] transition-colors font-['IBM_Plex_Mono'] text-sm"
              data-testid="form-cancel"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-[#4f9eff] text-[#0d0f14] rounded-sm hover:bg-[#4f9eff]/90 transition-colors font-['IBM_Plex_Mono'] text-sm font-bold"
              data-testid="form-submit"
            >
              Guardar
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Dominios;
